import type { JSX } from "react";
import { useI18n } from "../i18n/I18nContext";
import type { GuardedSignOut } from "../session/signOutRequest";
import { Alert } from "./Alert";
import { cx } from "./classNames";
import styles from "./ui.module.css";

/** Properties of {@link SignOutControls}. */
export interface SignOutControlsProps {
  /** The sign-out of the page, from `useGuardedSignOut`. */
  readonly signOut: GuardedSignOut;
}

/**
 * „Abmelden“ of an account section with its error and its confirmation (F-57, F-74): while entries
 * are unsent, the confirmation names their number before the local data is deleted; a failed
 * sign-out says so. Used by the settings pages of the tenants and the account page of the operator
 * area (F-84), so both ask the same way.
 *
 * @param props - Component properties.
 * @returns The controls.
 */
export function SignOutControls(props: SignOutControlsProps): JSX.Element {
  const { t } = useI18n();
  const { signOut } = props;
  const controls = (
    <>
      {signOut.failed ? <Alert tone="danger">{t("auth.logoutFailed")}</Alert> : null}
      {signOut.confirming ? (
        <Alert tone="danger">
          <p>{t("auth.logoutPendingWarning", { count: signOut.pendingCount })}</p>
          <div className={styles["buttonGrid"]}>
            <button type="button" className={styles["button"]} onClick={signOut.cancel}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className={cx(styles["button"], styles["danger"])}
              onClick={signOut.confirm}
            >
              {t("auth.logoutConfirm")}
            </button>
          </div>
        </Alert>
      ) : (
        <button
          type="button"
          className={cx(styles["button"], styles["block"])}
          onClick={() => {
            void signOut.request();
          }}
        >
          {t("auth.logout")}
        </button>
      )}
    </>
  );

  return controls;
}
